import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Inbox, UserCheck, Users, UserX } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Card } from "@/components/ui/card";
import { DataTable, DataTableColumn } from "@/components/ui/data-table";
import { EmptyState } from "@/components/ui/empty-state";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/contexts/ToastContext";
import { getApiErrorMessage } from "@/lib/api-error";
import {
  supportApi,
  SupportTicket,
  SupportTicketStatus,
  TICKET_CATEGORY_LABELS,
  TICKET_STATUS_LABELS,
  formatPerson,
  ListSupportTicketsParams,
} from "@/lib/api/support";
import { TicketStatusBadge } from "@/components/support/TicketStatusBadge";

const PAGE_SIZE = 25;

type ScopeFilter = "ALL" | "MINE" | "UNASSIGNED";

const STATUS_FILTER_OPTIONS: { value: "ALL" | SupportTicketStatus; label: string }[] = [
  { value: "ALL", label: "All statuses" },
  { value: "PENDING_STAFF", label: TICKET_STATUS_LABELS.PENDING_STAFF },
  { value: "PENDING_PATRON", label: TICKET_STATUS_LABELS.PENDING_PATRON },
  { value: "OPEN", label: TICKET_STATUS_LABELS.OPEN },
  { value: "RESOLVED", label: TICKET_STATUS_LABELS.RESOLVED },
  { value: "CLOSED", label: TICKET_STATUS_LABELS.CLOSED },
];

function buildListParams(
  scope: ScopeFilter,
  status: "ALL" | SupportTicketStatus
): ListSupportTicketsParams {
  const params: ListSupportTicketsParams = { limit: PAGE_SIZE };
  if (status !== "ALL") params.status = status;
  if (scope === "MINE") params.mine = "true";
  if (scope === "UNASSIGNED") params.unassigned = "true";
  return params;
}

export default function SupportInboxPage() {
  const toast = useToast();
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [scope, setScope] = useState<ScopeFilter>("MINE");
  const [statusFilter, setStatusFilter] = useState<"ALL" | SupportTicketStatus>("PENDING_STAFF");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    supportApi
      .list(buildListParams(scope, statusFilter))
      .then((res) => {
        if (!cancelled) setTickets(res.data);
      })
      .catch((err) => {
        if (!cancelled) toast.error(getApiErrorMessage(err, "Failed to load support inbox"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [scope, statusFilter, toast]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tickets;
    return tickets.filter(
      (t) =>
        t.subject.toLowerCase().includes(q) ||
        t.ticketNumber.toLowerCase().includes(q) ||
        formatPerson(t.patron).toLowerCase().includes(q)
    );
  }, [tickets, search]);

  const emptyCopy = useMemo(() => {
    if (scope === "MINE") {
      return {
        title: "Nothing assigned to you",
        description:
          "You will see tickets here once you reply to one or a colleague assigns one to you.",
      };
    }
    if (scope === "UNASSIGNED") {
      return {
        title: "No unassigned tickets",
        description: "Every open ticket is already being handled by someone.",
      };
    }
    return { title: "Inbox is clear", description: "No tickets match the current filter." };
  }, [scope]);

  const columns: DataTableColumn<SupportTicket>[] = [
    {
      id: "number",
      header: "Ticket",
      width: "w-32",
      cell: (t) => (
        <Link
          to={`/admin/support/${t.id}`}
          className="font-mono text-sm font-medium text-primary hover:underline"
        >
          {t.ticketNumber}
        </Link>
      ),
    },
    {
      id: "subject",
      header: "Subject",
      cell: (t) => (
        <div className="min-w-0">
          <Link
            to={`/admin/support/${t.id}`}
            className="line-clamp-1 font-medium text-slate-900 hover:underline"
          >
            {t.subject}
          </Link>
          <div className="mt-0.5 text-xs text-slate-500">
            {TICKET_CATEGORY_LABELS[t.category]}
            {t.databaseName ? ` · ${t.databaseName}` : ""}
          </div>
        </div>
      ),
    },
    {
      id: "patron",
      header: "Patron",
      width: "w-44",
      hideOnMobile: true,
      cell: (t) => <span className="text-sm text-slate-700">{formatPerson(t.patron)}</span>,
    },
    {
      id: "assignee",
      header: "Assignee",
      width: "w-44",
      hideOnMobile: true,
      cell: (t) => {
        const assignee = t.assigneeStaff ?? t.assigneeUser ?? null;
        return (
          <span className="text-sm text-slate-500">
            {assignee ? formatPerson(assignee) : "Unassigned"}
          </span>
        );
      },
    },
    {
      id: "status",
      header: "Status",
      width: "w-44",
      cell: (t) => <TicketStatusBadge status={t.status} />,
    },
    {
      id: "updated",
      header: "Last activity",
      width: "w-40",
      hideOnMobile: true,
      cell: (t) => (
        <span className="text-xs text-slate-500">{new Date(t.lastMessageAt).toLocaleString()}</span>
      ),
    },
  ];

  return (
    <PageContainer width="wide">
      <PageHeader
        title="Support inbox"
        description="Patron inquiries, technical support, and database access requests."
        breadcrumbItems={[{ label: "Dashboard", href: "/dashboard" }, { label: "Support inbox" }]}
        titleSize="md"
        searchQuery={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by ticket, subject, or patron…"
        actions={
          <div className="w-full sm:w-56">
            <Select
              value={statusFilter}
              onValueChange={(v) => setStatusFilter(v as "ALL" | SupportTicketStatus)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_FILTER_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        }
      />

      <Tabs value={scope} onValueChange={(v) => setScope(v as ScopeFilter)}>
        <TabsList className="mb-4 bg-slate-100/80 p-1">
          <TabsTrigger
            value="MINE"
            className="gap-2 data-[state=active]:bg-white data-[state=active]:shadow-sm"
          >
            <UserCheck className="h-4 w-4" /> Assigned to me
          </TabsTrigger>
          <TabsTrigger
            value="UNASSIGNED"
            className="gap-2 data-[state=active]:bg-white data-[state=active]:shadow-sm"
          >
            <UserX className="h-4 w-4" /> Unassigned
          </TabsTrigger>
          <TabsTrigger
            value="ALL"
            className="gap-2 data-[state=active]:bg-white data-[state=active]:shadow-sm"
          >
            <Users className="h-4 w-4" /> All tickets
          </TabsTrigger>
        </TabsList>
      </Tabs>

      <Card className="overflow-hidden">
        <DataTable
          columns={columns}
          data={filtered}
          rowKey={(t) => t.id}
          loading={loading}
          emptyState={
            <EmptyState
              icon={<Inbox className="h-6 w-6 text-slate-400" />}
              title={emptyCopy.title}
              description={emptyCopy.description}
            />
          }
        />
      </Card>
    </PageContainer>
  );
}
